import {
  AutoCompleteSearchInput,
  BackgroundIcon,
  ConfirmationModal,
  getHighlightedText,
  getTranslatedText
} from '@nile/components';
import {
  Button,
  ButtonGroup,
  DisplayText,
  Heading,
  ResourceItem,
  ResourceList,
  TextStyle
} from '@superops/ui-design-system';
import { Checkbox } from '@shopify/polaris';
import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { ReactSortable } from 'react-sortablejs';
import { FormattedMessage, injectIntl } from 'react-intl';
import ReorderIcon from '@nile/banyan/public/svg/ic_reorder.svg';
import { SVGIcon } from '@superops/super-icons';

const FieldLabel = styled.span`
  font-weight: 500;
  margin-left: 14px;
`;

function SelectionSheet(props) {
  const {
    intl,
    sections,
    meta,
    actions,
    template,
    canReorder,
    isPromptConfirmation,
    className,
    allowSameItemSelection,
    dynamicHeight,
    noCheckbox,
    noSearchBar,
    additionalSections,
    hideSelectedItems = false,
    hideSelectionSheetDoneBtn = false,
    isRunbookAction = false,
    disableScroll = false,
    removeSelectionClass = false,
    adaptHeight = false
  } = props;
  console.log('props.selectedItems: ', props.selectedItems);
  const [selectedItems, setSelectedItems] = useState(props.selectedItems || []);
  const [list, setList] = useState(props.selectedItems || []);
  const listByRef = React.useRef(1);
  listByRef.current = list;
  const [selectedItemsReference, setSelectedItemsReference] = useState({});
  const [filterExpression, setFilterExpression] = useState('');

  /**
   * Default render for selected column fields
   * @param {Array} selectedItems
   * @param {Object} item
   * @returns {ReactNode}
   */

  function hidePreviousSelectedUsers(item) {
    if (
      hideSelectedItems &&
      props.selectedItems?.find((u) => u.userId === item.userId)
    ) {
      return true;
    }
    return false;
  }
  function renderdDefaultSelectedItem(selectedItems, item) {
    const { label, id } = item;
    const selectedIds = selectedItems.map((el) => el.id);
    if (hidePreviousSelectedUsers(item)) {
      return false;
    }
    return (
      <ResourceItem
        id={item.id}
        accessibilityLabel="sample"
        onClick={() => {
          if (item.disable) return;
          selectedIds.indexOf(item.id) !== -1 &&
            selectedIds.splice(selectedIds.indexOf(item.id), 1);
          handleUnselection(selectedIds);
        }}
      >
        {template ? (
          <>
            {noCheckbox ? (
              <span className="text-with-no-checkbox">{label}</span>
            ) : (
              <div className="flex flex-align-center">
                <Checkbox
                  id={id}
                  name={id}
                  checked
                  disabled={item.disable}
                  onChange={(checked, value) => {
                    selectedIds.splice(selectedIds.indexOf(value), 1);
                    handleUnselection(selectedIds);
                  }}
                />
                {template(item)}
              </div>
            )}
          </>
        ) : (
          <TextStyle>
            {noCheckbox ? (
              <span className="text-with-no-checkbox">{label}</span>
            ) : (
              <Checkbox
                label={<FieldLabel>{label}</FieldLabel>}
                id={id}
                name={id}
                checked
                onChange={(checked, value) => {
                  selectedIds.splice(selectedIds.indexOf(value), 1);
                  handleUnselection(selectedIds);
                }}
              />
            )}
          </TextStyle>
        )}
      </ResourceItem>
    );
  }

  function renderItem(type, sectionIndex, item) {
    const { label, id } = item;
    return (
      <ResourceItem
        id={item.id}
        accessibilityLabel={`sample`}
        onClick={() => {
          if (item.disable) return;
          handleSelectedItems(sectionIndex, [id]);
        }}
      >
        <div data-qa={item.id} superid={item.id}>
          <DisplayText element="h3">
            {template ? (
              <>
                {noCheckbox ? (
                  <span className="text-with-no-checkbox">{label}</span>
                ) : (
                  <div className="flex flex-align-center">
                    <Checkbox
                      id={id}
                      name={id}
                      disabled={item.disable}
                      onChange={() => handleSelectedItems(sectionIndex, [id])}
                    />
                    {template(item)}
                  </div>
                )}
              </>
            ) : filterExpression && type === 'available' ? (
              <TextStyle>
                {noCheckbox ? (
                  <span className="text-with-no-checkbox">{label}</span>
                ) : (
                  <Checkbox
                    label={
                      <FieldLabel>
                        {getHighlightedText(label, filterExpression)}
                      </FieldLabel>
                    }
                    id={id}
                    name={id}
                    onChange={() => handleSelectedItems(sectionIndex, [id])}
                  />
                )}
              </TextStyle>
            ) : (
              <TextStyle>
                {noCheckbox ? (
                  <span className="text-with-no-checkbox">{label}</span>
                ) : (
                  <Checkbox
                    label={<FieldLabel>{label}</FieldLabel>}
                    id={id}
                    name={id}
                    onChange={() => handleSelectedItems(sectionIndex, [id])}
                  />
                )}
              </TextStyle>
            )}
          </DisplayText>
        </div>
      </ResourceItem>
    );
  }

  function renderSelectedItem(type, item, currentLi) {
    const { name, label } = item;
    const handleRemoval = (i, currentList) => {
      const updatedSelectedItems = listByRef.current.filter(
        (el) => el.id !== i.id
      );
      setList(updatedSelectedItems);
      const updatedIds = updatedSelectedItems.map((el) => el.id);
      handleUnselection(updatedIds);
    };
    return (
      <ResourceItem
        id={item.id}
        accessibilityLabel={`sample`}
        onClick={() => {
          if (item.disable) return;
          selectedIds.splice(selectedIds.indexOf(item.id), 1);
          handleUnselection(selectedIds);
        }}
      >
        <div data-qa={item.id} superid={item.id}>
          <DisplayText element="h3">
            {template ? (
              <>
                {canReorder && (
                  <span
                    className="dragHandle mr8 cursor-move"
                    style={{ opacity: '0.5' }}
                  >
                    <ReorderIcon />
                  </span>
                )}

                {noCheckbox ? (
                  <span className="text-with-no-checkbox">{label}</span>
                ) : (
                  <Checkbox
                    label={template(item)}
                    id={label}
                    name={label}
                    checked
                    disabled={item.disable}
                    onChange={handleRemoval.bind(this, item, currentLi)}
                  />
                )}
              </>
            ) : filterExpression && type === 'available' ? (
              <TextStyle>
                {getHighlightedText(label, filterExpression)}
              </TextStyle>
            ) : (
              <TextStyle>
                <div className="flex center">
                  <span
                    className="dragHandle mr8 cursor-move"
                    style={{ opacity: '0.5' }}
                  >
                    <ReorderIcon />
                  </span>

                  {noCheckbox ? (
                    <span className="text-with-no-checkbox">{label}</span>
                  ) : (
                    <Checkbox
                      label={label}
                      id={label}
                      name={label}
                      checked
                      disabled={item.disable}
                      onChange={handleRemoval.bind(this, item, currentLi)}
                    />
                  )}
                </div>
              </TextStyle>
            )}
          </DisplayText>
        </div>
      </ResourceItem>
    );
  }

  useEffect(() => {
    if (
      (hideSelectionSheetDoneBtn && props.isRunbookAction) ||
      props.AiSettings
    ) {
      props.updateParent(selectedItems);
    }
  }, [selectedItems]);

  const handleSelectedItems = (sectionIndex, selectedItem) => {
    let selectedSectionItems = sections[sectionIndex].items.find(
      (el) => el.id === selectedItem[0]
    );
    const newSelectedItems = selectedItems.concat([selectedSectionItems]);
    setSelectedItems(newSelectedItems);
    setList(newSelectedItems);
    setSelectedItemsReference({
      ...selectedItemsReference,
      [sectionIndex]: selectedItem
    });
  };

  const handleUnselection = (remainingSelectedItems) => {
    if (remainingSelectedItems.length) {
      setSelectedItems(
        listByRef.current.filter((el) => {
          return remainingSelectedItems.some((item) => item === el.id);
        })
      );
    } else {
      setSelectedItems([]);
    }
  };

  const updateParent = () => {
    props.updateParent(selectedItems);
    if (props?.toggleSheetActive) props.toggleSheetActive();
    else actions.toggleSheetActive();
  };

  const handleDone = () => {
    requestAnimationFrame(updateParent);
  };

  const debouncedFilter = useCallback((value) => {
    setFilterExpression(value.toLowerCase());
  });

  const stopEvents = (e) => {
    e.stopPropagation();
    e.preventDefault();
  };

  const associateModalActivator = (
    <Button primary>
      <FormattedMessage id="admin.Technicianrole.done" />
    </Button>
  );

  const getHoverconfig = () => {
    const { hoverConfig } = props;
    if (hoverConfig) {
      return hoverConfig;
    }
    return {
      background: 'rgba(0, 0, 0, 0.04)'
    };
  };

  const hasContent =
    (sections?.length > 0 && sections.filter((el) => el.items?.length > 0))
      ?.length > 0 || selectedItems?.length > 0;

  let SectionHandler = (section, sectionIndex) => {
    const renderItems = section.items.filter((el) => {
      if (filterExpression) {
        // Don't filter selected items if allow same item selection is supported.
        if (allowSameItemSelection) {
          return (
            el.label && el.label.toLowerCase().indexOf(filterExpression) > -1
          );
        }
        return (
          el.label &&
          el.label.toLowerCase().indexOf(filterExpression) > -1 &&
          !selectedItems.some((item) => item.id === el.id || item === el.id)
        );
      }
      // Don't filter selected items if allow same item selection is supported.
      return (
        allowSameItemSelection ||
        !selectedItems.some((item) => item.id === el.id || item === el.id)
      );
    });
    return (
      <SectionHolder
        dynamicHeight={dynamicHeight}
        selectedItemsHeight={selectedItems?.length * 50 || 0}
        name={section.header}
        className="flex fcol mb10"
      >
        <Heading element="h3" weight="Bold">
          {section.header}
        </Heading>
        {section.component || ''}
        <div className="selection-sheet-list">
          <ResourceList
            items={renderItems}
            renderItem={renderItem.bind(this, 'available', sectionIndex)}
          />
        </div>
        {renderItems.length === 0 ? (
          <div>
            {' '}
            <FormattedMessage id="admin.Technicianrole.associate.nodata" />
          </div>
        ) : (
          ''
        )}
      </SectionHolder>
    );
  };

  if (props?.sectionHandler) {
    SectionHandler = props?.sectionHandler;
  }
  function isShowSelectedItemHeading() {
    if (
      hideSelectedItems &&
      props.selectedItems?.length === selectedItems?.length
    ) {
      return false;
    }
    return true;
  }

  return (
    <SelectionWrapper
      className={`${!disableScroll && !adaptHeight && 'vh100'} ${
        removeSelectionClass ? '' : 'selection-sheet vw30'
      } ${className || ''} ${isRunbookAction && 'min-w100'}`}
      onClick={stopEvents}
      hoverConfig={getHoverconfig()}
      dynamicHeight={dynamicHeight}
    >
      <div className="flex fcol">
        {!hideSelectionSheetDoneBtn && (
          <div
            className="flex"
            data-qa="selectinSheetDoneBtn"
            superid="selectinSheetDoneBtn"
          >
            <Heading element="h1">{meta.heading}</Heading>
            <div className={`fx mlauto ${isRunbookAction && ' mb-20'}`}>
              {isPromptConfirmation ? (
                <ConfirmationModal
                  modalActivator={associateModalActivator}
                  description={getTranslatedText(
                    intl,
                    'admin.role.associateUsers.description'
                  )}
                  handleConfirmation={handleDone}
                  title={getTranslatedText(
                    intl,
                    'admin.role.associateUsers.header'
                  )}
                />
              ) : (
                <ButtonGroup spacing="loose">
                  {props.isRunbookAction && props.handleNodeDelete && (
                    <Button plainSecondary onClick={props.handleNodeDelete}>
                      <div className="danger">
                        <FormattedMessage id="common.delete" />
                      </div>
                    </Button>
                  )}
                  {props.isRunbookAction && props.handleNodeCancel && (
                    <Button plainSecondary onClick={props.handleNodeCancel}>
                      <FormattedMessage id="common.cancel" />
                    </Button>
                  )}
                  <Button
                    primary
                    onClick={handleDone}
                    disabled={
                      props.isRunbookAction && selectedItems.length === 0
                    }
                    {...(props.isRunbookAction && {
                      icon: <SVGIcon name="Save" />
                    })}
                  >
                    {props?.isRunbookAction ? (
                      <FormattedMessage id="common.save" />
                    ) : (
                      <FormattedMessage id="admin.Technicianrole.done" />
                    )}
                  </Button>
                </ButtonGroup>
              )}
            </div>
          </div>
        )}

        {additionalSections || null}

        {hasContent && !noSearchBar && !disableScroll && (
          <AutoCompleteSearchInput
            debouncedFilter={debouncedFilter}
            noMargin={props?.isRunbookAction}
          />
        )}

        <div
          className={`${
            dynamicHeight ? '' : !disableScroll && 'scroll-in '
          } ${noSearchBar && 'mt-20'}`}
        >
          {!props?.sectionHandler && selectedItems.length > 0 && (
            <div className="mb10 gen" name={'selected-items'}>
              {isShowSelectedItemHeading() && (
                <Heading element="h3" weight="Bold">
                  {props?.meta?.selectedTitle || 'Selected items'}
                </Heading>
              )}
              {canReorder ? (
                <ReactSortable
                  className="sort-wrapper"
                  list={list}
                  handle=".dragHandle"
                  setList={(li) => {
                    setList(li);
                    setSelectedItems(li);
                  }}
                  animation={200}
                >
                  {list.map((item) => (
                    <SortItem key={item.id}>
                      {renderSelectedItem('selected', item, selectedItems)}
                    </SortItem>
                  ))}
                </ReactSortable>
              ) : (
                <ResourceList
                  items={selectedItems}
                  renderItem={renderdDefaultSelectedItem.bind(
                    this,
                    selectedItems
                  )}
                />
              )}
            </div>
          )}
          {sections.map(SectionHandler)}
        </div>
      </div>
    </SelectionWrapper>
  );
}

const SectionHolder = styled.div`
  height: ${(props) =>
    props.dynamicHeight
      ? `calc(100vh - ${props.selectedItemsHeight + 223}px)`
      : 'auto'};
  overflow: ${(props) => (props.dynamicHeight ? 'auto' : '')};
  .mt-20 {
    margin-top: 20px;
  }
`;

const SortItem = styled.div`
  font-size: 14px;
  font-weight: 500;
  letter-spacing: 0;
  line-height: 16px;
  cursor: pointer;
  border-radius: 4px;
  .Polaris-Choice__Control {
    margin-right: 23px !important;
  }
  .Polaris-Choice__Label {
    font-weight: 500 !important;
  }
`;

const SelectionWrapper = styled.div`
  padding: 12px;
  overflow: ${(props) => (props.dynamicHeight ? 'hidden' : 'auto')};

  .text-with-no-checkbox {
    color: rgba(0, 0, 0, 0.8) !important
    font-weight: 500;
    font-size: 14px;
  }
  .Search-Input {
    margin-left: 0px !important;
    margin-right: 0px !important;
  }
  .Polaris-Choice {
    display: inline-flex;
    justify-content: flex-start;
    /* padding: 0.4rem 0; */
    cursor: pointer;
    height: 24px;
    align-items: center;
  }
`;

export default injectIntl(SelectionSheet);
